import { Component, ElementRef, HostBinding, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core'
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router'
import { AuthService } from './services/auth.service'
import { select, Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import {
  Account,
  AccountGuard,
  addChannelAdjustmentComplete,
  DataCheckerService,
  GroupRatesChangedComplete,
  isSomething,
  LocalStorage,
  RatesChangedComplete,
  selectAccountsLoaded,
  selectCurrentAccount,
  selectSomeOnce,
  selectUserById,
  SessionStorage,
  SubdomainGuard,
  Toaster,
  updateChannelAdjustmentComplete,
  User,
  UserStorage,
} from '@tokeet-frontend/tv3-platform'
import { debounceTime, delay, filter, map, switchMap, switchMapTo, take, tap, throttleTime } from 'rxjs/operators'
import * as fromRoot from '@tv3/store/state'
import { IntercomService } from '@tv3/services/intercom.service'
import { AppRoutesService } from '@tv3/services/utils/routes.service'
import { WootricService } from '@tv3/services/wootric.service'
import { VisitTV3 } from './store/user-preferences/user-preferences.actions'
import {
  selectUserPreferences,
  selectUserPreferencesLoaded,
} from '@tv3/store/user-preferences/user-preferences.selectors'
import * as Sentry from '@sentry/browser'
import { combineLatest, fromEvent, interval, merge, of } from 'rxjs'
import { RefreshInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { selectActiveProductPlanView, selectActiveTokeetPlanView } from '@tv3/store/plan/plan.selectors'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { GoogleTagManagerService } from './services/google-tag-manager.service'
import { RoutingService } from '@tv3/services/routing.service'
import { PhoneNumberRequestDialogService } from './shared/phone/phone-number-request-dialog/phone-number-request-dialog.service'
import { PlanSubscriptionView } from './store/plan/plan.view'
import { ADMIN_MASQUERADE_FLAG_KEY, USER_FROM_TV3_FLAG_KEY } from './services/utils/storage-prefix'

import 'sortable-tablesort/sortable.min.js'
import { ClipboardService, IClipboardResponse } from 'ngx-clipboard'
import { RentalGuard } from './guards/rental.guard'
import { UserPreferencesGuard } from './guards/user-preference.guard'
import { ActivePlanGuard } from './guards/active-plan.guard'
import { ChannelGuard } from '@tv3/guards/channel.guard'
import { PreferencesGuard } from './guards/preferences.guard'
import { EntityTagsGuard } from '@tokeet-frontend/tags'
import { AppSubscribedComplete } from '@tokeet-frontend/billing'
import { LoadActivePlans } from './store/plan/plan.actions'
import {
  CreateGapNightAutomationComplete,
  CreateLastMinuteAutomationComplete,
  CreateMinStayAutomationComplete,
  UpdateGapNightAutomationComplete,
  UpdateLastMinuteAutomationComplete,
  UpdateMinStayAutomationComplete,
} from '@tokeet-frontend/rate-automations'
import { ProductsForPlan } from './store/plan/plan.model'
import { RategenieService } from './services/rategenie.service'
import { GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { environment } from '@tv3/environments/environment'

declare const window: any
export const CHUNK_ERROR_KEY = 'CHUNK_ERROR_KEY'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild('mainSection', { static: true }) mainSection: ElementRef

  pageNavigationEnd$ = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
  pageNavigationStart$ = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationStart))

  authenticatedPageLoading$ = this.pageNavigationStart$.pipe(
    filter((data: NavigationStart) => this.appRoutesService.isProtected(data.url)),
    filter(() => this.authService.isAuthenticated)
  )

  authenticatedPageLoaded$ = this.pageNavigationEnd$.pipe(
    filter((data: NavigationEnd) => this.appRoutesService.isProtected(data.url)),
    filter(() => this.authService.isAuthenticated)
  )

  isFirstPaid$ = this.routingService.isFirstPaid()
  delinquent$ = this.routingService.isDelinquent()
  // Only one delinquent banner shows at a time, so the calendar offset stays one banner high.
  smartDevicesDelinquent$ = combineLatest([
    this.routingService.isDelinquent(),
    this.routingService.isSmartDevicesDelinquent(),
  ]).pipe(map(([isDelinquent, isSmartDevicesDelinquent]) => !isDelinquent && isSmartDevicesDelinquent))

  isNoGlobalAlert = false
  appMainClass = ''

  @HostBinding('class.no-top-navbar')
  isTopNavbarDisabled = false

  isUserFromTV3 = false

  @ViewChild('onboardingTmpl') onboardingTmpl: TemplateRef<unknown>
  onboardingOverlayRef: OverlayRef = this.overlay.create({ hasBackdrop: false })
  onboardingPopupHiddenStoreKey = 'onboardingPopupHidden'

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
    private actions: Actions,
    public appRoutesService: AppRoutesService,
    private authService: AuthService,
    private intercomService: IntercomService,
    private googleTagManagerService: GoogleTagManagerService,
    private storage: UserStorage,
    private routingService: RoutingService,
    private toaster: Toaster,
    private overlay: Overlay,
    private clipboard: ClipboardService,
    private wootricService: WootricService,
    private amplitudeService: AmplitudeService,
    private dataGuard: DataCheckerService,
    private sessionStorage: SessionStorage,
    private rategenieService: RategenieService,
    private phoneNumberRequestDialog: PhoneNumberRequestDialogService
  ) {}

  get isMainPageScrollable() {
    const routes: [string, boolean][] = [['rates', true]]

    return !routes.find((route) => this.router.isActive(route[0], route[1]))
  }

  get isReadonly() {
    return this.authService.isReadOnly()
  }

  get isHouseStaff() {
    return this.authService.hasRole('staff')
  }

  ngOnInit() {
    const oldHandler = this.router.errorHandler
    // Replace route error handler
    this.router.errorHandler = (err: any) => {
      // Check if there is an error loading the chunk
      if (err.stack && err.stack.indexOf('Error: Loading chunk') >= 0) {
        // Check if is the first time the error happened
        if (this.storage.get(CHUNK_ERROR_KEY) !== err.stack) {
          // Save the last error to avoid an infinite reload loop if the chunk really does not exists after reload
          this.storage.set(CHUNK_ERROR_KEY, err.stack)
          this.toaster.info($localize`New version of the app detected, reloading...`)
          setTimeout(() => {
            window.location.reload(true)
          }, 2500)
        } else {
          // The chunk really does not exist after reload
          console.error(`We cannot find the chunk...`)
        }
      }
      // Run original handler
      oldHandler(err)
    }

    // Initialize Amplitude
    this.amplitudeService.init()

    if (this.authService.isAuthenticated) {
      this.intercomService.boot(this.authService.user)
      this.googleTagManagerService.update(this.authService.user)
      this.amplitudeService.setUser(this.authService.user)
      this.wootricService.run(this.authService.user)

      Sentry.configureScope((scope) => {
        const id = this.authService.user?.account?.toString() || ''
        scope.setUser({ id })
      })
    }

    this.authService.verify().subscribe()

    this.pageNavigationEnd$
      .pipe(
        tap(() => (this.mainSection.nativeElement.scrollTop = 0)),
        switchMap(() =>
          combineLatest([this.routingService.isDelinquent(), this.routingService.isSmartDevicesDelinquent()]).pipe(
            map(([isDelinquent, isSmartDevicesDelinquent]) => isDelinquent || isSmartDevicesDelinquent)
          )
        )
      )
      .subscribe((isDelinquent) => {
        const classes = []
        if (this.appRoutesService.isProtected()) {
          classes.push('main-body')
        }

        if (!this.isMainPageScrollable) {
          classes.push('no-scroll')
        }

        if (this.isReadonly) {
          classes.push('readonly-alert')
        }
        if (isDelinquent) {
          classes.push('delinquent-alert')
        }
        this.appMainClass = classes.join(' ')
        this.amplitudeService.logPageView()
      })

    this.authenticatedPageLoading$.pipe(take(1)).subscribe(() => {
      this.listenRatesChange()
    })
    // Track if default guards have been loaded
    let defaultGuardsLoaded = false

    this.authenticatedPageLoading$.subscribe((navEvent: NavigationStart) => {
      // Skip default guards for pages that have their own specific guard requirements
      const skipDefaultGuards = ['/billing/info', '/rentals']
      const shouldSkipDefaultGuards = skipDefaultGuards.some((page) => navEvent.url.includes(page))

      // Load default guards if:
      // 1. We're not on a skip page AND they haven't been loaded yet
      // 2. OR we're navigating away from a skip page (to ensure data is loaded when needed)
      if (!shouldSkipDefaultGuards && !defaultGuardsLoaded) {
        const guardsToCheck = [
          RentalGuard,
          ChannelGuard,
          AccountGuard,
          PreferencesGuard,
          UserPreferencesGuard,
          EntityTagsGuard,
          SubdomainGuard,
        ]

        console.log('LOADED')
        this.dataGuard.check(guardsToCheck)
        defaultGuardsLoaded = true
      }
    })

    this.authenticatedPageLoaded$
      .pipe(
        tap(() => {
          this.isUserFromTV3 = this.sessionStorage.get(USER_FROM_TV3_FLAG_KEY, false)
        }),
        switchMap(() =>
          this.store.pipe(
            select(selectUserPreferencesLoaded),
            filter((isLoaded) => isLoaded && !this.authService.hasRole('readOnly')),
            take(1)
          )
        ),
        switchMap(() => this.store.pipe(selectSomeOnce(selectUserPreferences)))
      )
      .subscribe((preferences) => {
        if (!preferences.tv3Login) {
          // this.videoDialogService.open()
          this.store.dispatch(VisitTV3())
        }
      })

    this.authenticatedPageLoaded$.pipe(delay(2000), take(1)).subscribe(() => {
      this.dataGuard.check([ActivePlanGuard])
      if (!this.storage.get(this.onboardingPopupHiddenStoreKey)) {
        setTimeout(() => this.showOnboardingPopup(), 5000)
      }
    })

    this.authenticatedPageLoaded$
      .pipe(
        switchMapTo(this.store.pipe(select(selectAccountsLoaded))),
        filter((loaded) => !!loaded),
        switchMapTo(this.store.pipe(select(selectCurrentAccount))),
        take(1)
      )
      .subscribe((account) => {
        const user = this.authService.user
        const phone = isSomething(user.phone) ? user.phone : account.phone
        this.intercomService.update({ ...user, phone })
        this.googleTagManagerService.update({ ...user, phone } as User)
        this.amplitudeService.setUser({ ...user, phone } as User)
      })

    this.authenticatedPageLoaded$
      .pipe(
        take(1),
        // refresh on a timer, but skip ticks while the tab is hidden and catch up
        // as soon as the window regains focus
        switchMap(() => merge(interval(90 * 1000), fromEvent(window, 'focus'))),
        filter(() => document.visibilityState === 'visible'),
        // only poll on pages that actually display the inquiry list — this used to fire on
        // every page (calendar, rentals, channels, settings, …) whether or not any inquiry
        // UI was visible
        filter(() => ['/inquiries', '/dashboard'].some((page) => this.router.url.includes(page))),
        throttleTime(15 * 1000)
      )
      .subscribe(() => {
        this.store.dispatch(RefreshInquiries())
      })

    this.authenticatedPageLoaded$
      .pipe(
        tap(() => document.body.classList.add('app-loaded')),
        switchMap(() => this.store.pipe(select(selectUserById(this.authService.user.id)))),
        map((u) => u || this.authService.user)
      )
      .subscribe((user) => {
        if (user?.settings?.tooltip === 0) {
          document.body.classList.add('app-tooltip-disabled')
        } else {
          document.body.classList.remove('app-tooltip-disabled')
        }
      })

    combineLatest([
      this.store.pipe(select(selectCurrentAccount), filter(isSomething)),
      this.store.pipe(select(selectActiveTokeetPlanView), filter(isSomething)),
    ])
      .pipe(
        take(1),
        filter(([_, sub]: [Account, PlanSubscriptionView]) => sub.isPaid && sub.status === 'active'),
        switchMap(([account]) => {
          const user = this.authService.user
          const masquerade: boolean = this.sessionStorage.get(ADMIN_MASQUERADE_FLAG_KEY)
          if (!masquerade && this.authService.isAdmin() && (!account.phone || !user.phone)) {
            return this.phoneNumberRequestDialog.open().afterClosed()
          } else {
            return of(true)
          }
        })
      )
      .subscribe()

    this.clipboard.copyResponse$.subscribe((res: IClipboardResponse) => {
      if (res.isSuccess) {
        this.toaster.success($localize`Copied to clipboard`, $localize`Success`)
      }
    })

    this.pageNavigationEnd$.subscribe(() => {
      const data = this.route.snapshot.firstChild?.data
      const hideTopNavbar = !!data?.noTopNavbar
      this.isTopNavbarDisabled = this.authService.hasRole('staff') || this.authService.hasRole('owner') || hideTopNavbar
    })

    this.actions.pipe(ofType(AppSubscribedComplete)).subscribe(() => {
      this.store.dispatch(LoadActivePlans({}))
    })
  }

  listenRatesChange() {
    let isAccountSynced = false
    let isTokeetSynced = false
    this.rategenieService.checkAccountSync().subscribe((isSynced) => {
      isAccountSynced = isSynced
    })
    this.rategenieService.checkTokeetSync().subscribe((isSynced) => {
      isTokeetSynced = isSynced
    })

    combineLatest([
      this.store.pipe(select(selectActiveProductPlanView(ProductsForPlan.Rategenie))),
      merge(
        this.actions.pipe(
          ofType(GroupRatesChangedComplete),
          filter((action) => !action.isRentalDeleted)
        ),
        this.actions.pipe(
          ofType(
            CreateMinStayAutomationComplete,
            UpdateMinStayAutomationComplete,
            CreateLastMinuteAutomationComplete,
            UpdateLastMinuteAutomationComplete,
            CreateGapNightAutomationComplete,
            UpdateGapNightAutomationComplete,
            addChannelAdjustmentComplete,
            updateChannelAdjustmentComplete
          )
        ),
        this.actions.pipe(
          ofType(RatesChangedComplete),
          switchMap((action) => this.rategenieService.checkRentalSync(action.rentalId))
        )
      ),
    ])
      .pipe(debounceTime(500))
      .subscribe(([plan, isSynced]) => {
        if (plan?.isActive && isAccountSynced && isTokeetSynced && isSynced === true) {
          this.toaster.info(
            $localize`You are currently using Rategenie. Please log in to Rategenie to update your rates.`,
            '',
            { timeOut: 30 * 1000 }
          )
        } else {
          this.toaster.info(
            $localize`Updated rates are scheduled to be pushed to your connected channels, provided rental rate mapping is set up and auto-sync is enabled.`,
            '',
            { timeOut: 30 * 1000 }
          )
        }
      })
  }

  onReturnToTV3() {
    const pathname = location.pathname
    const cleanedPathname = pathname.replace(/^\/[a-z]{2}\//, '/')
    const url = `${environment.legacyTv3Url}/login?token=${this.authService.token}&stayInTv3=1&redirectURL=${cleanedPathname}`
    window.open(url, '_self')
  }

  showOnboardingPopup() {
    const portal = new TemplatePortal(this.onboardingTmpl, this._viewContainerRef)
    const position = new GlobalPositionStrategy()
    position.centerHorizontally().left('96px').bottom('16px')
    this.onboardingOverlayRef.updatePositionStrategy(position)
    this.onboardingOverlayRef.attach(portal)
  }

  onDismissOnboarding() {
    this.onboardingOverlayRef.detach()
    this.storage.set(this.onboardingPopupHiddenStoreKey, true)
  }
}
